// Meer > Meldingen (spec 4.6), the frame of every Meer screen and the backup warning (L40).
import { useEffect, useState, type ReactNode } from "react";
import { partOfGroup } from "@shared/morning";
import { useLiveQuery } from "dexie-react-hooks";
import { ArrowLeft, Bell, BellOff, Lock } from "lucide-react";
import { toast } from "sonner";
import { sortBlocks } from "@shared/rhythm";
import { displayTime, planningDate } from "@shared/time";
import { SyncBadge } from "../components/SyncBadge";
import { Button, cn } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { setBlockNotify } from "../lib/appointments";
import { db } from "../lib/db";
import { navigate } from "../lib/hooks";
import { disablePush, enablePush, pushSupported, type PushServerState } from "../lib/push";

export function SubScreen({ title, children }: { title: string; children: ReactNode }) {
  return (
    <main className="mx-auto max-w-xl px-4 pt-[max(1.25rem,env(safe-area-inset-top))] pb-44">
      <a
        href="/meer"
        onClick={(e) => {
          e.preventDefault();
          navigate("/meer");
        }}
        className="-ml-2 mb-2 inline-flex min-h-11 items-center gap-1 rounded-xl px-2 text-pine"
      >
        <ArrowLeft aria-hidden className="size-5" />
        Meer
      </a>
      <header className="mb-5 flex flex-wrap items-end justify-between gap-2">
        <h1 className="text-[1.75rem] font-bold">{title}</h1>
        <SyncBadge />
      </header>
      {children}
    </main>
  );
}

/** Meldingen: on or off for this device, test, quiet hours, which blocks ring. */
export function NotificationsScreen() {
  const [state, setState] = useState<PushServerState | null>(null);
  const [quiet, setQuiet] = useState<{ quietStart: string; quietEnd: string } | null>(null);
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [offline, setOffline] = useState(false);
  const today = planningDate(new Date());

  useEffect(() => {
    let alive = true;
    Promise.all([api<PushServerState>("/account/push"), api<{ quietStart: string; quietEnd: string }>("/account/settings")])
      .then(([push, settings]) => {
        if (!alive) return;
        setState(push);
        setQuiet(settings);
        setOffline(false);
      })
      .catch(() => alive && setOffline(true));
    return () => {
      alive = false;
    };
  }, [version]);

  const types = useLiveQuery(async () => {
    const templates = (await db.day_templates.toArray())
      .filter((t) => !t.deleted_at && !t.archived_at)
      .sort((a, b) => a.position - b.position);
    const blocks = (await db.day_template_blocks.toArray()).filter((b) => !b.deleted_at);
    return templates.map((t) => ({
      template: t,
      // The evening planning rings once it exists (wave 5).
      blocks: sortBlocks(
        blocks.filter(
          (b) =>
            b.template_id === t.id &&
            b.slot_kind !== "planning" &&
            // The steps of the morning and evening lists ring one by one (B27, B32); other groups once, at their start.
            !(
              b.group_title &&
              !partOfGroup(b.group_title) &&
              b.position > 0 &&
              blocks.some((x) => x.template_id === t.id && x.group_title === b.group_title && x.position < b.position)
            ),
        ),
      ),
    }));
  });

  async function turnOn() {
    if (!state?.publicKey) return;
    setBusy(true);
    try {
      const result = await enablePush(state.publicKey);
      if (result === "denied") toast("Meldingen zijn geweigerd. Zet ze aan in de instellingen van je browser voor deze site.");
      else if (result === "unsupported") toast("Dit toestel ondersteunt geen meldingen.");
      else toast("Meldingen staan aan op dit toestel");
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Aanzetten is niet gelukt. Probeer het opnieuw.");
    } finally {
      setBusy(false);
      setVersion((v) => v + 1);
    }
  }

  async function turnOff() {
    setBusy(true);
    await disablePush().catch(() => {});
    setBusy(false);
    toast("Meldingen staan uit op dit toestel");
    setVersion((v) => v + 1);
  }

  async function test() {
    try {
      await api("/account/push/test", { body: {} });
      toast("Testmelding verstuurd");
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Versturen is niet gelukt.");
    }
  }

  async function saveQuiet() {
    if (!quiet) return;
    try {
      await api("/account/settings", { body: quiet });
      toast("Stille uren opgeslagen");
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Opslaan is niet gelukt.");
    }
  }

  const on = !!state?.thisDevice && typeof Notification !== "undefined" && Notification.permission === "granted";
  return (
    <SubScreen title="Meldingen">
      {offline && <p className="mb-4 text-ink-soft">Meldingen instellen kan alleen met verbinding.</p>}
      <section aria-labelledby="dit-toestel" className="mb-8 rounded-2xl border border-mist bg-surface p-4">
        <h2 id="dit-toestel" className="mb-2 flex items-center gap-2 text-lg font-semibold">
          {on ? <Bell aria-hidden className="size-5 text-pine" /> : <BellOff aria-hidden className="size-5 text-ink-soft" />}
          {on ? "Meldingen werken op dit toestel" : "Meldingen staan uit op dit toestel"}
        </h2>
        {!pushSupported() && (
          <p className="mb-3 text-ink-soft">Dit toestel of deze browser kan geen meldingen tonen. Installeer de app op je beginscherm.</p>
        )}
        {state && !state.enabled && <p className="mb-3 text-ink-soft">Meldingen zijn op de server nog niet ingesteld.</p>}
        <div className="flex flex-col gap-2">
          {on ? (
            <>
              <Button onClick={test}>Testmelding</Button>
              <Button variant="secondary" onClick={turnOff} disabled={busy}>
                Uitzetten op dit toestel
              </Button>
            </>
          ) : (
            <Button onClick={turnOn} disabled={busy || !state?.enabled || !pushSupported()}>
              Meldingen aanzetten
            </Button>
          )}
        </div>
        {state && state.devices.length > 0 && (
          <p className="mt-3 text-sm text-ink-soft">
            Aan op {state.devices.length === 1 ? "1 toestel" : `${state.devices.length} toestellen`}.
          </p>
        )}
      </section>

      {quiet && (
        <section aria-labelledby="stil" className="mb-8">
          <h2 id="stil" className="mb-1 text-lg font-semibold">
            Stille uren
          </h2>
          <p className="mb-3 text-ink-soft">Geen meldingen in deze tijd, ook niet voor afspraken.</p>
          <div className="mb-3 grid grid-cols-2 gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-sm text-ink-soft">Vanaf</span>
              <input
                type="time"
                value={quiet.quietStart}
                onChange={(e) => setQuiet({ ...quiet, quietStart: e.target.value })}
                className="min-h-12 rounded-xl border-2 border-mist bg-paper px-3 text-ink"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm text-ink-soft">Tot</span>
              <input
                type="time"
                value={quiet.quietEnd}
                onChange={(e) => setQuiet({ ...quiet, quietEnd: e.target.value })}
                className="min-h-12 rounded-xl border-2 border-mist bg-paper px-3 text-ink"
              />
            </label>
          </div>
          <Button variant="secondary" className="w-full" onClick={saveQuiet}>
            Stille uren opslaan
          </Button>
        </section>
      )}

      <section aria-labelledby="blokken">
        <h2 id="blokken" className="mb-1 text-lg font-semibold">
          Melding bij de start van een blok
        </h2>
        <p className="mb-3 text-ink-soft">Afspraken krijgen altijd hun eigen melding vooraf.</p>
        {(types ?? []).map(({ template, blocks }) => (
          <details key={template.id} className="mb-2 rounded-2xl border border-mist bg-surface">
            <summary className="flex min-h-12 cursor-pointer items-center px-3 font-semibold">{template.name}</summary>
            <ul className="px-1 pb-2">
              {blocks.map((b) => (
                <li key={b.id}>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={b.notify}
                    onClick={() => void setBlockNotify(b.id, !b.notify, today)}
                    className="flex min-h-12 w-full items-center justify-between gap-3 rounded-xl px-2 text-left"
                  >
                    <span className="min-w-0 [overflow-wrap:anywhere]">
                      <span className="mr-2 text-ink-soft tabular">{displayTime(b.start_local)}</span>
                      {b.group_title && !partOfGroup(b.group_title) ? b.group_title : b.title}
                    </span>
                    <span
                      aria-hidden
                      className={cn("relative h-7 w-12 shrink-0 rounded-full transition-colors", b.notify ? "bg-pine" : "bg-mist")}
                    >
                      <span className={cn("absolute top-1 size-5 rounded-full bg-paper transition-all", b.notify ? "left-6" : "left-1")} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </details>
        ))}
      </section>
    </SubScreen>
  );
}

/** A warning when the last backup or restore test is old or failed (L40); only for PK. */
export function BackupWarning() {
  const [warning, setWarning] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api<{ warning: string | null }>("/account/backup")
      .then((r) => alive && setWarning(r.warning))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  if (!warning) return null;
  return (
    <p role="status" className="mb-6 flex items-start gap-2 rounded-2xl border-2 border-sun bg-surface p-3">
      <Lock aria-hidden className="mt-0.5 size-5 shrink-0 text-ink" />
      <span>
        <span className="font-semibold">Back up: </span>
        {warning} Kijk met <code className="text-sm">npm run vps:status</code>.
      </span>
    </p>
  );
}
